import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader, Panel } from "../ds";
import { fmtDateTime, dubaiDayKey } from "../lib/time";
import { PageErrorBoundary } from "../components/PageErrorBoundary";
import { AlertTriangle, CheckCircle2, XCircle, Server, HardDrive, Cpu, Archive, Globe } from "lucide-react";

function Meter({ value, label, testId }) {
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  const color = pct >= 95 ? "#ef4444" : pct >= 80 ? "#f59e0b" : "var(--cobalt)";
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between font-mono text-xs tabular-nums mb-1">
        <span className="text-[color:var(--muted)] uppercase tracking-wider text-[10px]">{label}</span>
        <span className="text-[color:var(--fg)]">{pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 rounded-full bg-[color:var(--line)] overflow-hidden">
        <div style={{ width: `${pct}%`, background: color, height: "100%" }} />
      </div>
    </div>
  );
}

function MonitoringInner() {
  const { data, isLoading } = useQuery({
    queryKey: ["monitoring"],
    queryFn: async () => (await api.get("/monitoring")).data,
    refetchInterval: 30000,
  });

  if (isLoading) return <div className="p-10 text-[color:var(--muted)]">Loading…</div>;

  const snap = data?.snapshot;
  const stale = !!data?.is_stale;

  if (!snap) {
    return (
      <div className="p-10 max-w-[1200px] mx-auto space-y-4" data-testid="monitoring-empty">
        <PageHeader title="IT" subtitle="Servisler, kaynaklar, yedekler, site kontrolleri" />
        <Panel label="MONITORING">
          <p className="text-sm text-[color:var(--muted)]">
            Henüz veri yok. IT Lead ajanının snapshot göndermesi bekleniyor.
          </p>
        </Panel>
      </div>
    );
  }

  const services = snap.services || [];
  const sites = snap.sites || [];
  const backups = snap.backups || [];
  const alerts = snap.alerts || [];
  const disk = snap.disk;
  const mem = snap.memory;

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto space-y-5">
      <PageHeader title="IT" subtitle={`Last check: ${fmtDateTime(snap.checked_at)}`} />

      {stale && (
        <div
          className="flex items-start gap-2 border border-amber-500/30 bg-amber-500/5 rounded-md p-3"
          data-testid="monitoring-stale"
        >
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-[color:var(--muted)]">
            Veri eski — IT Lead son 15 dakikada bir snapshot göndermedi.
          </p>
        </div>
      )}

      {/* Services + resources */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel label="SERVICES" testId="mon-services">
          {services.length === 0
            ? <p className="text-sm text-[color:var(--muted)]">No services reported.</p>
            : (
            <ul className="space-y-1.5 text-sm" data-testid="mon-services-list">
              {services.map((s) => (
                <li key={s.name} className="flex items-center justify-between gap-2 py-1 border-b border-[color:var(--line)] last:border-b-0">
                  <span className="flex items-center gap-2">
                    <Server className="h-3.5 w-3.5 text-[color:var(--muted)]" />
                    <span className="font-mono text-xs">{s.name}</span>
                  </span>
                  <span className="flex items-center gap-2">
                    {s.state === "active"
                      ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      : <XCircle className="h-3.5 w-3.5 text-rose-500" />}
                    <span className={`font-mono text-[10px] uppercase ${s.state === "active" ? "text-emerald-500" : "text-rose-500"}`}>
                      {s.state}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel label="RESOURCES" testId="mon-resources">
          <div className="space-y-4">
            {disk ? (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <HardDrive className="h-3.5 w-3.5 text-[color:var(--muted)]" />
                  <Meter value={disk.used_pct} label={`Disk · ${disk.free_gb.toFixed(1)} GB free`} testId="meter-disk" />
                </div>
              </div>
            ) : <p className="text-xs text-[color:var(--muted)]">No disk data.</p>}
            {mem ? (
              <div className="flex items-center gap-2">
                <Cpu className="h-3.5 w-3.5 text-[color:var(--muted)]" />
                <div className="flex-1"><Meter value={mem.used_pct} label="Memory" testId="meter-memory" /></div>
              </div>
            ) : <p className="text-xs text-[color:var(--muted)]">No memory data.</p>}
          </div>
        </Panel>
      </div>

      {/* Sites + backups */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel label="SITES" testId="mon-sites">
          {sites.length === 0
            ? <p className="text-sm text-[color:var(--muted)]">No sites.</p>
            : (
            <ul className="space-y-1.5 text-sm">
              {sites.map((s) => (
                <li key={s.url} className="flex items-center justify-between gap-2 py-1 border-b border-[color:var(--line)] last:border-b-0">
                  <span className="flex items-center gap-2 min-w-0">
                    <Globe className="h-3.5 w-3.5 text-[color:var(--muted)]" />
                    <span className="font-mono text-xs truncate max-w-[260px]">{s.url}</span>
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[11px] tabular-nums">
                    <span className={s.ok ? "text-emerald-500" : "text-rose-500"}>{s.status_code}</span>
                    {s.latency_ms != null && <span className="text-[color:var(--muted)]">{Math.round(s.latency_ms)} ms</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel label="BACKUPS" testId="mon-backups">
          {backups.length === 0
            ? <p className="text-sm text-[color:var(--muted)]">No backups reported.</p>
            : (
            <ul className="space-y-1.5 text-sm">
              {backups.map((b, i) => (
                <li key={`${b.name}-${i}`} className="flex items-center justify-between gap-2 py-1 border-b border-[color:var(--line)] last:border-b-0">
                  <span className="flex items-center gap-2 min-w-0">
                    <Archive className="h-3.5 w-3.5 text-[color:var(--muted)]" />
                    <span className="font-mono text-xs truncate max-w-[220px]">{b.name}</span>
                  </span>
                  <span className="flex items-center gap-3 font-mono text-[11px] tabular-nums">
                    <span className="text-[color:var(--muted)]">{b.size_mb.toFixed(0)} MB</span>
                    <span className="text-[color:var(--muted)]">{fmtDateTime(b.at)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {/* Alerts */}
      <Panel label="ALERTS" testId="mon-alerts">
        {alerts.length === 0
          ? <p className="text-sm text-[color:var(--muted)]">No alerts.</p>
          : (
          <ul className="space-y-2 text-sm">
            {alerts.map((a, i) => {
              const color = a.level === "crit" ? "rose" : a.level === "warn" ? "amber" : "zinc";
              return (
                <li
                  key={i}
                  className={`flex items-start gap-2 border border-${color}-500/30 bg-${color}-500/5 rounded-md p-2`}
                  data-testid={`mon-alert-${i}`}
                >
                  <AlertTriangle className={`h-3.5 w-3.5 shrink-0 mt-0.5 text-${color}-500`} />
                  <div className="flex-1">
                    <span className={`font-mono text-[10px] uppercase mr-2 text-${color}-500`}>{a.level}</span>
                    <span className="text-xs">{a.text}</span>
                    {a.at && <span className="font-mono text-[10px] text-[color:var(--muted)] ml-2">{fmtDateTime(a.at)}</span>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}

export default function Monitoring() {
  return <PageErrorBoundary><MonitoringInner /></PageErrorBoundary>;
}
